import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, HttpResponse, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixDocumentManagementEditorDeploymentsV1Deployment from './document-management-editor-deployments-v1-deployment-deployments.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';

/** Deployment is the main entity of Deployments. */
export interface Deployment
  extends DeploymentDescriptionOneOf,
    DeploymentCreationPublishTypeOneOf {
  /** Deployment description */
  editorRevision?: EditorRevision;
  /** No publish */
  noPublish?: CreationPublishTypeNone;
  /** Publish Site */
  sitePublish?: CreationPublishTypeSite;
  /** Publish Site's RC */
  rcPublish?: CreationPublishTypeRC;
  /**
   * Deployment ID
   * @format GUID
   */
  _id?: string;
  /** Deployment type */
  deploymentType?: DeploymentTypeWithLiterals;
  /**
   * The label of the deployment.
   * @maxLength 250
   */
  label?: string | null;
  /**
   * Represents the time this Deployment was last modified
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision */
  revision?: string | null;
  /**
   * All pipelines that implement DeploymentPipelineProvider interface
   * @maxSize 30
   */
  pipelines?: PipelineResult[];
  /** The pipelines status of the deployment */
  deploymentPipelinesStatus?: DeploymentPipelinesStatusWithLiterals;
  /**
   * Represents the time this Deployment was created
   * @readonly
   */
  _createdDate?: Date | null;
  /** The editor session that created the deployment */
  editorSession?: EditorSession;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
}

/** @oneof */
export interface DeploymentDescriptionOneOf {
  /** Deployment description */
  editorRevision?: EditorRevision;
}

/** @oneof */
export interface DeploymentCreationPublishTypeOneOf {
  /** No publish */
  noPublish?: CreationPublishTypeNone;
  /** Publish Site */
  sitePublish?: CreationPublishTypeSite;
  /** Publish Site's RC */
  rcPublish?: CreationPublishTypeRC;
}

export enum DeploymentType {
  UNKNOWN = 'UNKNOWN',
  EDITOR_REVISION = 'EDITOR_REVISION',
}

/** @enumType */
export type DeploymentTypeWithLiterals =
  | DeploymentType
  | 'UNKNOWN'
  | 'EDITOR_REVISION';

export interface EditorRevision {
  /**
   * Branch's Id
   * @format GUID
   */
  branchId?: string | null;
  /** Revision's Id */
  siteRevision?: string;
}

export interface PipelineResult {
  /**
   * The deployment pipeline implementer id as appears in the SPI Config
   * @format GUID
   */
  deploymentPipelineId?: string;
  /** Pipeline status */
  pipelineStatus?: PipelineStatusWithLiterals;
  /**
   * The deployment pipeline task results
   * @maxSize 20
   */
  tasksResults?: TaskResult[];
}

export enum PipelineStatus {
  UNDEFINED = 'UNDEFINED',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  INVOKE_ERROR = 'INVOKE_ERROR',
  TIMEOUT = 'TIMEOUT',
  SKIPPED = 'SKIPPED',
}

/** @enumType */
export type PipelineStatusWithLiterals =
  | PipelineStatus
  | 'UNDEFINED'
  | 'IN_PROGRESS'
  | 'SUCCESS'
  | 'ERROR'
  | 'INVOKE_ERROR'
  | 'TIMEOUT'
  | 'SKIPPED';

export interface TaskResult {
  /**
   * Name of the task for internal references. For example: "user code bundling".
   * @minLength 1
   * @maxLength 250
   */
  taskName?: string;
  /** The execution status of the pipeline for the given deployment creation process */
  status?: TaskStatusWithLiterals;
}

export enum TaskStatus {
  UNDEFINED = 'UNDEFINED',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  RUNNING = 'RUNNING',
  ABORTED = 'ABORTED',
}

/** @enumType */
export type TaskStatusWithLiterals =
  | TaskStatus
  | 'UNDEFINED'
  | 'SUCCESS'
  | 'ERROR'
  | 'RUNNING'
  | 'ABORTED';

export enum DeploymentPipelinesStatus {
  UNDEFINED = 'UNDEFINED',
  /** No pipelines are defined for this deployment */
  NONE = 'NONE',
  /** Pipelines are invoked and in progress */
  IN_PROGRESS = 'IN_PROGRESS',
  /** Pipelines completed successfully */
  SUCCESS = 'SUCCESS',
  /** Pipelines completed with errors */
  ERROR = 'ERROR',
}

/** @enumType */
export type DeploymentPipelinesStatusWithLiterals =
  | DeploymentPipelinesStatus
  | 'UNDEFINED'
  | 'NONE'
  | 'IN_PROGRESS'
  | 'SUCCESS'
  | 'ERROR';

export interface EditorSession {
  /**
   * Editor Session Id
   * @format GUID
   */
  esi?: string | null;
  /**
   * Document services Origin
   * @maxLength 50
   */
  dsOrigin?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface CreationPublishTypeNone {}

export interface CreationPublishTypeSite {}

export interface CreationPublishTypeRC {
  /** The RC label provided on the deployment creation */
  rcLabel?: RCLabelWithLiterals;
  /** If true, during publishing the RC there will be a validation that there is at most 1 RC with the same label for this site's branch, and if such RC exists it will replace it with the new RC. */
  useUniqueLabel?: boolean;
  /**
   * number of minutes the RC will be published
   * @max 129600
   * @min 1
   */
  ttlMinutes?: number | null;
}

export enum RCLabel {
  /** Illegal default value, exception will be thrown if used */
  UNKNOWN = 'UNKNOWN',
  RELEASE_MANAGER = 'RELEASE_MANAGER',
  BLOCKS = 'BLOCKS',
  WIX_CLI = 'WIX_CLI',
  MOBILE_APP_BUILDER = 'MOBILE_APP_BUILDER',
  SEO = 'SEO',
  FEEDBACK = 'FEEDBACK',
  ACCESSIBILITY = 'ACCESSIBILITY',
  ENTERPRISE_APPROVALS = 'ENTERPRISE_APPROVALS',
}

/** @enumType */
export type RCLabelWithLiterals =
  | RCLabel
  | 'UNKNOWN'
  | 'RELEASE_MANAGER'
  | 'BLOCKS'
  | 'WIX_CLI'
  | 'MOBILE_APP_BUILDER'
  | 'SEO'
  | 'FEEDBACK'
  | 'ACCESSIBILITY'
  | 'ENTERPRISE_APPROVALS';

export interface DeploymentPipelineUpdatedMessage {
  /**
   * The deployment id
   * @format GUID
   */
  deploymentId?: string;
}

export interface CreateDeploymentRequest
  extends CreateDeploymentRequestBasedOnOneOf,
    CreateDeploymentRequestPublishMethodOneOf {
  /** Based on a specific Editor Revision. */
  specificVersion?: EditorRevision;
  /** Based on the latest state of a branch. */
  latestFromBranch?: LatestFromBranch;
  /** Publish RC */
  publishSiteRcMethod?: PublishSiteRCMethod;
  /**
   * Deployment's label.
   * @maxLength 250
   */
  label?: string;
  /**
   * Deployments attributes.
   * @maxSize 1
   */
  deploymentAttributes?: DeploymentAttribute[];
  /** Supporting publish on the creation flow. Please specify the publish type and leave empty in case you don't want to publish */
  publishType?: PublishTypeWithLiterals;
  /** Session info */
  sessionInfo?: SessionInfo;
}

/** @oneof */
export interface CreateDeploymentRequestBasedOnOneOf {
  /** Based on a specific Editor Revision. */
  specificVersion?: EditorRevision;
  /** Based on the latest state of a branch. */
  latestFromBranch?: LatestFromBranch;
}

/** @oneof */
export interface CreateDeploymentRequestPublishMethodOneOf {
  /** Publish RC */
  publishSiteRcMethod?: PublishSiteRCMethod;
}

export interface DeploymentAttribute extends DeploymentAttributeAttributeOneOf {
  /** GridAppId attribute. */
  gridAppIdAttribute?: GridAppIdAttribute;
}

/** @oneof */
export interface DeploymentAttributeAttributeOneOf {
  /** GridAppId attribute. */
  gridAppIdAttribute?: GridAppIdAttribute;
}

export interface GridAppIdAttribute {
  /**
   * The gridAppId attribute would like to override/append.
   * @maxLength 100
   */
  gridAppId?: string;
}

export enum PublishType {
  /** No publish */
  NONE = 'NONE',
  /** Publish Site */
  SITE = 'SITE',
  /** Publish Site's RC - Notice that PublishSiteRCMethod should be provided as well. */
  RC = 'RC',
}

/** @enumType */
export type PublishTypeWithLiterals = PublishType | 'NONE' | 'SITE' | 'RC';

export interface SessionInfo {
  /**
   * Editor session id
   * @format GUID
   */
  esi?: string | null;
  /**
   * Ds origin
   * @maxLength 50
   */
  dsOrigin?: string | null;
}

export interface LatestFromBranch {
  /**
   * Branch to get the latest state from.
   * @format GUID
   */
  branchId?: string | null;
}

export interface PublishSiteRCMethod {
  /** The label of this RC */
  rcLabel?: RCLabelWithLiterals;
  /** If true, during publishing the RC there will be a validation that there is at most 1 RC with the same label for this site's branch, and if such RC exists it will replace it with the new RC. */
  useUniqueLabel?: boolean;
  /**
   * number of minutes the RC will be published
   * @max 129600
   * @min 1
   */
  ttlMinutes?: number | null;
}

export interface CreateDeploymentResponse {
  /** The created Deployment */
  deployment?: Deployment;
}

export interface GetDeploymentRequest {
  /**
   * Id of the Deployment to retrieve
   * @format GUID
   */
  deploymentId: string;
}

export interface GetDeploymentResponse {
  /** The retrieved Deployment */
  deployment?: Deployment;
}

export interface PublishDeploymentRequest {
  /**
   * Id of the Deployment to publish
   * @format GUID
   */
  deploymentId: string;
  /** If provided - will publish even if deployment's pipeline is not successfully completed */
  skipPipelineCheck?: boolean | null;
}

export interface PublishDeploymentResponse {
  /** The published Deployment */
  deployment?: Deployment;
}

export interface PublishRCDeploymentRequest {
  /**
   * Id of the Deployment to publish
   * @format GUID
   */
  deploymentId: string;
  /** The label of this RC */
  rcLabel?: RCLabelWithLiterals;
  /** If provided - will publish even if deployment's pipeline is not successfully completed */
  skipPipelineCheck?: boolean | null;
  /** If true, during publishing the RC there will be a validation that there is at most 1 RC with the same label for this site's branch, and if such RC exists it will replace it with the new RC. */
  useUniqueLabel?: boolean;
  /**
   * number of minutes the RC will be published
   * @max 129600
   * @min 1
   */
  ttlMinutes?: number | null;
}

export interface PublishRCDeploymentResponse {
  /** The published Deployment */
  deployment?: Deployment;
}

export interface ListDeploymentsRequest {}

export interface ListDeploymentsResponse {
  /** The retrieved Deployments */
  deployments?: Deployment[];
}

export interface QueryDeploymentsRequest {
  /** Cursor query message. */
  query: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryDeploymentsResponse {
  /** Results from query. */
  deployments?: Deployment[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface ReportDeploymentPipelineResultRequest {
  /**
   * The deployment creation process id, should match the id that was passed in the invoke() call by the SPI Host
   * @format GUID
   */
  deploymentId: string;
  /**
   * The deployment pipeline implementer id as appears in the SPI Config
   * @format GUID
   */
  deploymentPipelineId: string;
  /**
   * The deployment pipeline task results
   * @maxSize 20
   */
  tasksResults?: TaskResult[];
}

export interface ReportDeploymentPipelineResultResponse {}

export interface GetDeploymentPipelineDescriptionRequest {
  /**
   * The deployment id
   * @format GUID
   */
  deploymentId: string;
  /**
   * The deployment pipelines implementers ids as appears in the SPI Config to be called for getting descriptions.
   * @format GUID
   * @minSize 1
   * @maxSize 30
   */
  deploymentPipelinesIds: string[];
}

export interface GetDeploymentPipelineDescriptionResponse {
  /**
   * Full description on the tasks each provider done on the specific deployment_id (description per task).
   * @minSize 30
   */
  pipelinesDescription?: PipelineDescription[];
}

export interface PipelineDescription {
  /**
   * The deployment pipeline id (spi implementers).
   * @format GUID
   */
  deploymentPipelineId?: string;
  /**
   * description per task
   * @maxSize 20
   */
  tasksDescriptions?: TaskDescription[];
}

export interface TaskDescription {
  /**
   * Name of the task for internal references. For example: "user code bundling".
   * @minLength 1
   * @maxLength 250
   */
  taskName?: string;
  /**
   * Description about the tasks results.
   * @maxLength 10000
   */
  description?: string | null;
  /** Task status */
  status?: TaskStatusWithLiterals;
}

export interface Empty {}

export interface UpdateExtendedFieldsRequest {
  /** ID of the entity to update. */
  _id: string;
  /** Identifier for the app whose extended fields are being updated. */
  namespace: string;
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

export interface UpdateExtendedFieldsResponse {
  /** Updated Deployment. */
  deployment?: Deployment;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';
/** @docsIgnore */
export type CreateDeploymentApplicationErrors = {
  code?: 'UNSUPPORTED_EDITOR';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type PublishDeploymentApplicationErrors = {
  code?: 'PUBLISH_NOT_ALLOWED';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type PublishRcDeploymentApplicationErrors = {
  code?: 'PUBLISH_NOT_ALLOWED';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type ReportDeploymentPipelineResultApplicationErrors = {
  code?: 'NO_PIPELINE_IN_PROGRESS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type GetDeploymentPipelineDescriptionApplicationErrors = {
  code?: 'PIPELINE_NOT_COMPLETED';
  description?: string;
  data?: Record<string, any>;
};

/**
 * Creates a new Deployment
 * @public
 * @documentationMaturity preview
 * @requiredField options.basedOn
 * @permissionId EDITOR.DEPLOYMENT_CREATE
 * @returns The created Deployment
 * @fqn wix.editor.deployments.v1.DeploymentsService.CreateDeployment
 */
export async function createDeployment(
  options?: CreateDeploymentOptions
): Promise<
  NonNullablePaths<
    Deployment,
    | `editorRevision.siteRevision`
    | `rcPublish.rcLabel`
    | `rcPublish.useUniqueLabel`
    | `_id`
    | `deploymentType`
    | `pipelines`
    | `pipelines.${number}.deploymentPipelineId`
    | `pipelines.${number}.pipelineStatus`
    | `deploymentPipelinesStatus`,
    4
  > & {
    __applicationErrorsType?: CreateDeploymentApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    label: options?.label,
    deploymentAttributes: options?.deploymentAttributes,
    publishType: options?.publishType,
    sessionInfo: options?.sessionInfo,
    specificVersion: options?.specificVersion,
    latestFromBranch: options?.latestFromBranch,
    publishSiteRcMethod: options?.publishSiteRcMethod,
  });

  const reqOpts =
    ambassadorWixDocumentManagementEditorDeploymentsV1Deployment.createDeployment(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.deployment!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          label: '$[0].label',
          deploymentAttributes: '$[0].deploymentAttributes',
          publishType: '$[0].publishType',
          sessionInfo: '$[0].sessionInfo',
          specificVersion: '$[0].specificVersion',
          latestFromBranch: '$[0].latestFromBranch',
          publishSiteRcMethod: '$[0].publishSiteRcMethod',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreateDeploymentOptions
  extends CreateDeploymentOptionsBasedOnOneOf,
    CreateDeploymentOptionsPublishMethodOneOf {
  /**
   * Deployment's label.
   * @maxLength 250
   */
  label?: string;
  /**
   * Deployments attributes.
   * @maxSize 1
   */
  deploymentAttributes?: DeploymentAttribute[];
  /** Supporting publish on the creation flow. Please specify the publish type and leave empty in case you don't want to publish */
  publishType?: PublishTypeWithLiterals;
  /** Session info */
  sessionInfo?: SessionInfo;
  /** Based on a specific Editor Revision. */
  specificVersion?: EditorRevision;
  /** Based on the latest state of a branch. */
  latestFromBranch?: LatestFromBranch;
  /** Publish RC */
  publishSiteRcMethod?: PublishSiteRCMethod;
}

/** @oneof */
export interface CreateDeploymentOptionsBasedOnOneOf {
  /** Based on a specific Editor Revision. */
  specificVersion?: EditorRevision;
  /** Based on the latest state of a branch. */
  latestFromBranch?: LatestFromBranch;
}

/** @oneof */
export interface CreateDeploymentOptionsPublishMethodOneOf {
  /** Publish RC */
  publishSiteRcMethod?: PublishSiteRCMethod;
}

/**
 * Get a Deployment by id
 * @param deploymentId - Id of the Deployment to retrieve
 * @public
 * @documentationMaturity preview
 * @requiredField deploymentId
 * @permissionId EDITOR.DEPLOYMENT_READ
 * @returns The retrieved Deployment
 * @fqn wix.editor.deployments.v1.DeploymentsService.GetDeployment
 */
export async function getDeployment(
  deploymentId: string
): Promise<
  NonNullablePaths<
    Deployment,
    | `editorRevision.siteRevision`
    | `rcPublish.rcLabel`
    | `rcPublish.useUniqueLabel`
    | `_id`
    | `deploymentType`
    | `pipelines`
    | `pipelines.${number}.deploymentPipelineId`
    | `pipelines.${number}.pipelineStatus`
    | `deploymentPipelinesStatus`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    deploymentId: deploymentId,
  });

  const reqOpts =
    ambassadorWixDocumentManagementEditorDeploymentsV1Deployment.getDeployment(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.deployment!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { deploymentId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['deploymentId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Publish a Deployment
 * @param deploymentId - Id of the Deployment to publish
 * @public
 * @documentationMaturity preview
 * @requiredField deploymentId
 * @permissionId EDITOR.DEPLOYMENT_PUBLISH
 * @fqn wix.editor.deployments.v1.DeploymentsService.PublishDeployment
 */
export async function publishDeployment(
  deploymentId: string,
  options?: PublishDeploymentOptions
): Promise<
  NonNullablePaths<
    PublishDeploymentResponse,
    | `deployment.editorRevision.siteRevision`
    | `deployment.rcPublish.rcLabel`
    | `deployment.rcPublish.useUniqueLabel`
    | `deployment._id`
    | `deployment.deploymentType`
    | `deployment.pipelines`
    | `deployment.pipelines.${number}.deploymentPipelineId`
    | `deployment.pipelines.${number}.pipelineStatus`
    | `deployment.deploymentPipelinesStatus`,
    5
  > & {
    __applicationErrorsType?: PublishDeploymentApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    deploymentId: deploymentId,
    skipPipelineCheck: options?.skipPipelineCheck,
  });

  const reqOpts =
    ambassadorWixDocumentManagementEditorDeploymentsV1Deployment.publishDeployment(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          deploymentId: '$[0]',
          skipPipelineCheck: '$[1].skipPipelineCheck',
        },
        singleArgumentUnchanged: false,
      },
      ['deploymentId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PublishDeploymentOptions {
  /** If provided - will publish even if deployment's pipeline is not successfully completed */
  skipPipelineCheck?: boolean | null;
}

/**
 * Publish an RC Deployment
 * @param deploymentId - Id of the Deployment to publish
 * @public
 * @documentationMaturity preview
 * @requiredField deploymentId
 * @permissionId EDITOR.DEPLOYMENT_PUBLISH
 * @fqn wix.editor.deployments.v1.DeploymentsService.PublishRCDeployment
 */
export async function publishRcDeployment(
  deploymentId: string,
  options?: PublishRcDeploymentOptions
): Promise<
  NonNullablePaths<
    PublishRCDeploymentResponse,
    | `deployment.editorRevision.siteRevision`
    | `deployment.rcPublish.rcLabel`
    | `deployment.rcPublish.useUniqueLabel`
    | `deployment._id`
    | `deployment.deploymentType`
    | `deployment.pipelines`
    | `deployment.pipelines.${number}.deploymentPipelineId`
    | `deployment.pipelines.${number}.pipelineStatus`
    | `deployment.deploymentPipelinesStatus`,
    5
  > & {
    __applicationErrorsType?: PublishRcDeploymentApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    deploymentId: deploymentId,
    rcLabel: options?.rcLabel,
    skipPipelineCheck: options?.skipPipelineCheck,
    useUniqueLabel: options?.useUniqueLabel,
    ttlMinutes: options?.ttlMinutes,
  });

  const reqOpts =
    ambassadorWixDocumentManagementEditorDeploymentsV1Deployment.publishRcDeployment(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          deploymentId: '$[0]',
          rcLabel: '$[1].rcLabel',
          skipPipelineCheck: '$[1].skipPipelineCheck',
          useUniqueLabel: '$[1].useUniqueLabel',
          ttlMinutes: '$[1].ttlMinutes',
        },
        singleArgumentUnchanged: false,
      },
      ['deploymentId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PublishRcDeploymentOptions {
  /** The label of this RC */
  rcLabel?: RCLabelWithLiterals;
  /** If provided - will publish even if deployment's pipeline is not successfully completed */
  skipPipelineCheck?: boolean | null;
  /** If true, during publishing the RC there will be a validation that there is at most 1 RC with the same label for this site's branch, and if such RC exists it will replace it with the new RC. */
  useUniqueLabel?: boolean;
  /**
   * number of minutes the RC will be published
   * @max 129600
   * @min 1
   */
  ttlMinutes?: number | null;
}

/**
 * List Deployments
 * @public
 * @documentationMaturity preview
 * @permissionId EDITOR.DEPLOYMENT_READ
 * @fqn wix.editor.deployments.v1.DeploymentsService.ListDeployments
 */
export async function listDeployments(): Promise<
  NonNullablePaths<
    ListDeploymentsResponse,
    | `deployments`
    | `deployments.${number}.editorRevision.siteRevision`
    | `deployments.${number}.rcPublish.rcLabel`
    | `deployments.${number}.rcPublish.useUniqueLabel`
    | `deployments.${number}._id`
    | `deployments.${number}.deploymentType`
    | `deployments.${number}.deploymentPipelinesStatus`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts =
    ambassadorWixDocumentManagementEditorDeploymentsV1Deployment.listDeployments(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Query Deployments
 * @public
 * @documentationMaturity preview
 * @permissionId EDITOR.DEPLOYMENT_READ
 * @fqn wix.editor.deployments.v1.DeploymentsService.QueryDeployments
 */
export function queryDeployments(): DeploymentsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    Deployment,
    'CURSOR',
    QueryDeploymentsRequest,
    QueryDeploymentsResponse
  >({
    func: async (payload: QueryDeploymentsRequest) => {
      const reqOpts =
        ambassadorWixDocumentManagementEditorDeploymentsV1Deployment.queryDeployments(
          payload
        );

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryDeploymentsRequest['query']) => {
      const args = [query, {}] as [QueryDeploymentsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryDeploymentsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.deployments,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface DeploymentsQueryResult extends QueryCursorResult {
  items: Deployment[];
  query: DeploymentsQueryBuilder;
  next: () => Promise<DeploymentsQueryResult>;
  prev: () => Promise<DeploymentsQueryResult>;
}

export interface DeploymentsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | 'deploymentType'
      | 'label'
      | '_updatedDate'
      | 'pipelines.deploymentPipelineId'
      | 'pipelines.pipelineStatus'
      | 'pipelines.tasksResults.taskName'
      | 'pipelines.tasksResults.status'
      | 'deploymentPipelinesStatus'
      | '_createdDate',
    value: any
  ) => DeploymentsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName: '_updatedDate' | '_createdDate',
    value: any
  ) => DeploymentsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName: '_updatedDate' | '_createdDate',
    value: any
  ) => DeploymentsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName: '_updatedDate' | '_createdDate',
    value: any
  ) => DeploymentsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName: '_updatedDate' | '_createdDate',
    value: any
  ) => DeploymentsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName: '_updatedDate' | '_createdDate',
    value: any
  ) => DeploymentsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName: '_updatedDate' | '_createdDate',
    value: any[]
  ) => DeploymentsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName: '_updatedDate' | '_createdDate',
    value: any
  ) => DeploymentsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName: '_updatedDate' | '_createdDate',
    value: boolean
  ) => DeploymentsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<
      | 'deploymentType'
      | 'label'
      | '_updatedDate'
      | 'deploymentPipelinesStatus'
      | '_createdDate'
    >
  ) => DeploymentsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<
      | 'deploymentType'
      | 'label'
      | '_updatedDate'
      | 'deploymentPipelinesStatus'
      | '_createdDate'
    >
  ) => DeploymentsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => DeploymentsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => DeploymentsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<DeploymentsQueryResult>;
}

/**
 * The pipeline provider has to send this event when its execution has ended.
 * @param deploymentId - The deployment creation process id, should match the id that was passed in the invoke() call by the SPI Host
 * @param deploymentPipelineId - The deployment pipeline implementer id as appears in the SPI Config
 * @public
 * @documentationMaturity preview
 * @requiredField deploymentId
 * @requiredField deploymentPipelineId
 * @permissionId EDITOR.DEPLOYMENT_UPDATE
 * @fqn wix.editor.deployments.v1.DeploymentsService.ReportDeploymentPipelineResult
 */
export async function reportDeploymentPipelineResult(
  deploymentId: string,
  deploymentPipelineId: string,
  options?: ReportDeploymentPipelineResultOptions
): Promise<
  void & {
    __applicationErrorsType?: ReportDeploymentPipelineResultApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    deploymentId: deploymentId,
    deploymentPipelineId: deploymentPipelineId,
    tasksResults: options?.tasksResults,
  });

  const reqOpts =
    ambassadorWixDocumentManagementEditorDeploymentsV1Deployment.reportDeploymentPipelineResult(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          deploymentId: '$[0]',
          deploymentPipelineId: '$[1]',
          tasksResults: '$[2].tasksResults',
        },
        singleArgumentUnchanged: false,
      },
      ['deploymentId', 'deploymentPipelineId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ReportDeploymentPipelineResultOptions {
  /**
   * The deployment pipeline task results
   * @maxSize 20
   */
  tasksResults?: TaskResult[];
}

/**
 * Get Deployment Pipeline Description
 * @param deploymentId - The deployment id
 * @param deploymentPipelinesIds - The deployment pipelines implementers ids as appears in the SPI Config to be called for getting descriptions.
 * @public
 * @documentationMaturity preview
 * @requiredField deploymentId
 * @requiredField deploymentPipelinesIds
 * @permissionId EDITOR.DEPLOYMENT_READ
 * @fqn wix.editor.deployments.v1.DeploymentsService.GetDeploymentPipelineDescription
 */
export async function getDeploymentPipelineDescription(
  deploymentId: string,
  deploymentPipelinesIds: string[]
): Promise<
  NonNullablePaths<
    GetDeploymentPipelineDescriptionResponse,
    | `pipelinesDescription`
    | `pipelinesDescription.${number}.deploymentPipelineId`,
    4
  > & {
    __applicationErrorsType?: GetDeploymentPipelineDescriptionApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    deploymentId: deploymentId,
    deploymentPipelinesIds: deploymentPipelinesIds,
  });

  const reqOpts =
    ambassadorWixDocumentManagementEditorDeploymentsV1Deployment.getDeploymentPipelineDescription(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          deploymentId: '$[0]',
          deploymentPipelinesIds: '$[1]',
        },
        singleArgumentUnchanged: false,
      },
      ['deploymentId', 'deploymentPipelinesIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates extended fields of a Deployment without incrementing revision
 * @param _id - ID of the entity to update.
 * @param namespace - Identifier for the app whose extended fields are being updated.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField namespace
 * @requiredField options
 * @requiredField options.namespaceData
 * @permissionId EDITOR.DEPLOYMENT_UPDATE
 * @fqn wix.editor.deployments.v1.DeploymentsService.UpdateExtendedFields
 */
export async function updateExtendedFields(
  _id: string,
  namespace: string,
  options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
): Promise<
  NonNullablePaths<
    UpdateExtendedFieldsResponse,
    | `deployment.editorRevision.siteRevision`
    | `deployment.rcPublish.rcLabel`
    | `deployment.rcPublish.useUniqueLabel`
    | `deployment._id`
    | `deployment.deploymentType`
    | `deployment.pipelines`
    | `deployment.pipelines.${number}.deploymentPipelineId`
    | `deployment.pipelines.${number}.pipelineStatus`
    | `deployment.deploymentPipelinesStatus`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    namespace: namespace,
    namespaceData: options?.namespaceData,
  });

  const reqOpts =
    ambassadorWixDocumentManagementEditorDeploymentsV1Deployment.updateExtendedFields(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          id: '$[0]',
          namespace: '$[1]',
          namespaceData: '$[2].namespaceData',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'namespace', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateExtendedFieldsOptions {
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}
